/**
 * UranIA PA — API client admin skills (US-029).
 *
 * Wrapper su `fetch` per il catalogo skill esposto dal servizio
 * `services/skills-registry` (vedi `routes/skills.py`):
 *   - `GET /api/v1/skills`             -> elenco skill correnti
 *     (`current_version_pointer=true`) visibili al tenant del Principal
 *     (platform_admin vede anche le skill globali).
 *   - `GET /api/v1/skills/{skill_id}`  -> tutte le versioni della skill
 *     (metadata only). Il body in plaintext **non** transita su questo
 *     endpoint: lo schema server-side dichiara letteralmente che il
 *     body decifrato passa SOLO via `get_skill_body()` lato Gateway
 *     (cap. 13.1 + analisi §13 skill registry). Quando US-019+ esporra'
 *     l'endpoint dedicato `:body` con audit + RBAC, questo client
 *     verra' esteso senza modificare la firma `getSkill()`.
 *
 * Boundary:
 *   - Bearer JWT preso da `useAuthStore.accessToken` (in-memory, FR-F1-10).
 *   - `credentials: 'include'` per SSO cookie Keycloak (silent renew).
 *   - Problem+JSON RFC 9457 quando 4xx; fallback su `statusText` se il
 *     server non emette Problem valido.
 *   - Niente PII nei log: la pagina logga `subject` + `skill_id` ma non
 *     attributi free-text.
 */

import { loadSpaEnv } from '../env.ts';
import { useAuthStore } from '../auth/auth-store.ts';

export interface SkillSummaryDto {
  readonly skill_row_id: string;
  readonly skill_id: string;
  readonly version: string;
  readonly tenant_id: string | null;
  readonly name: string;
  readonly description: string | null;
  readonly is_core: boolean;
  readonly is_active: boolean;
  readonly current_version_pointer: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListSkillsDto {
  readonly items: readonly SkillSummaryDto[];
}

export interface GetSkillDto {
  readonly skill_id: string;
  readonly versions: readonly SkillSummaryDto[];
}

export interface SkillsApiErrorPayload {
  readonly status: number;
  readonly code: string | null;
  readonly title: string;
  readonly detail: string | null;
  readonly acrRequired?: readonly string[] | null;
}

export class SkillsApiError extends Error {
  public readonly status: number;
  public readonly code: string | null;
  public readonly detail: string | null;
  public readonly acrRequired: readonly string[] | null;

  constructor(payload: SkillsApiErrorPayload) {
    super(payload.title);
    this.name = 'SkillsApiError';
    this.status = payload.status;
    this.code = payload.code;
    this.detail = payload.detail;
    this.acrRequired = payload.acrRequired ?? null;
  }
}

function authHeader(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  if (token === null || token.length === 0) {
    return {};
  }
  return { Authorization: `Bearer ${token}` };
}

function buildUrl(path: string): string {
  const env = loadSpaEnv();
  const base = env.apiBaseUrl.replace(/\/+$/u, '');
  return `${base}${path}`;
}

interface ProblemBody {
  readonly title?: unknown;
  readonly detail?: unknown;
  readonly status?: unknown;
  readonly code?: unknown;
  readonly acr_required?: unknown;
}

function readStringArray(value: unknown): readonly string[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  return value.filter((v): v is string => typeof v === 'string');
}

async function parseProblem(response: Response): Promise<SkillsApiError> {
  let body: ProblemBody = {};
  try {
    body = (await response.json()) as ProblemBody;
  } catch {
    // server non ha emesso Problem+JSON: fallback su statusText.
  }
  const title =
    typeof body.title === 'string' && body.title.length > 0
      ? body.title
      : response.statusText || 'http_error';
  const detail = typeof body.detail === 'string' ? body.detail : null;
  const code = typeof body.code === 'string' ? body.code : null;
  return new SkillsApiError({
    status: response.status,
    code,
    title,
    detail,
    acrRequired: readStringArray(body.acr_required),
  });
}

async function requestJson(method: 'GET', path: string): Promise<unknown> {
  const init: RequestInit = {
    method,
    headers: {
      Accept: 'application/json',
      ...authHeader(),
    },
    credentials: 'include',
  };
  let response: Response;
  try {
    response = await fetch(buildUrl(path), init);
  } catch (err) {
    throw new SkillsApiError({
      status: 0,
      code: 'network_error',
      title: err instanceof Error ? err.message : 'network_error',
      detail: null,
    });
  }
  if (!response.ok) {
    throw await parseProblem(response);
  }
  if (response.status === 204) {
    return null;
  }
  return (await response.json()) as unknown;
}

export async function listSkills(): Promise<ListSkillsDto> {
  const raw = (await requestJson('GET', '/api/v1/skills')) as ListSkillsDto;
  return raw;
}

export async function getSkill(skillId: string): Promise<GetSkillDto> {
  const raw = (await requestJson(
    'GET',
    `/api/v1/skills/${encodeURIComponent(skillId)}`,
  )) as GetSkillDto;
  return raw;
}

// ---------------------------------------------------------------------------
// Write API: POST /api/v1/skills/{skill_id}/versions (US-019).
//
// In prod richiede platform_admin (skill core / tenant_id=null) o tenant_admin
// (skill tenant-scoped) + step-up MFA (acr=urn:mace:incommon:iap:silver).
// In dev locale lo step-up e' bypassato dalla flag env URANIA_SKIP_STEP_UP=true
// nel skills-registry (vedi services/skills-registry/.../routes/skills.py).
// ---------------------------------------------------------------------------

export interface CreateSkillVersionRequestDto {
  readonly version: string;
  readonly name: string;
  readonly description: string | null;
  readonly body: string;
  readonly tenant_id: string | null;
  readonly is_core: boolean;
}

export interface CreateSkillVersionResponseDto {
  readonly skill_row_id: string;
  readonly skill_id: string;
  readonly version: string;
  readonly tenant_id: string | null;
  readonly is_core: boolean;
  readonly audit_action: 'created' | 'updated';
  readonly outbox_event_id: string;
  readonly audit_event_id: string;
}

async function requestJsonBody(
  method: 'POST',
  path: string,
  payload: unknown,
): Promise<unknown> {
  const init: RequestInit = {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...authHeader(),
    },
    credentials: 'include',
    body: JSON.stringify(payload),
  };
  let response: Response;
  try {
    response = await fetch(buildUrl(path), init);
  } catch (err) {
    throw new SkillsApiError({
      status: 0,
      code: 'network_error',
      title: err instanceof Error ? err.message : 'network_error',
      detail: null,
    });
  }
  if (!response.ok) {
    throw await parseProblem(response);
  }
  if (response.status === 204) {
    return null;
  }
  return (await response.json()) as unknown;
}

export async function createOrUpdateSkillVersion(
  skillId: string,
  body: CreateSkillVersionRequestDto,
): Promise<CreateSkillVersionResponseDto> {
  return (await requestJsonBody(
    'POST',
    `/api/v1/skills/${encodeURIComponent(skillId)}/versions`,
    body,
  )) as CreateSkillVersionResponseDto;
}

// ---------------------------------------------------------------------------
// Attachments — file allegati a una skill (admin)
//
// La GET e' service-to-service friendly (no bearer richiesto): ritorna
// solo metadati. POST/DELETE richiedono bearer admin + step-up MFA.
// ---------------------------------------------------------------------------

export interface SkillAttachmentDto {
  readonly filename: string;
  readonly mime_type: string;
  readonly size_bytes: number;
  readonly uploaded_at: string;
  readonly uploaded_by: string | null;
  readonly descrizione: string;
}

export interface SkillAttachmentListDto {
  readonly skill_id: string;
  readonly items: readonly SkillAttachmentDto[];
  readonly total: number;
}

export async function listSkillAttachments(
  skillId: string,
): Promise<SkillAttachmentListDto> {
  return (await requestJson(
    'GET',
    `/api/v1/skills/${encodeURIComponent(skillId)}/attachments`,
  )) as SkillAttachmentListDto;
}

export interface UploadSkillAttachmentInput {
  readonly file: File;
  readonly descrizione?: string;
}

export async function uploadSkillAttachment(
  skillId: string,
  input: UploadSkillAttachmentInput,
): Promise<SkillAttachmentDto> {
  const form = new FormData();
  form.append('file', input.file);
  if (typeof input.descrizione === 'string' && input.descrizione.length > 0) {
    form.append('descrizione', input.descrizione);
  }
  const response = await fetch(
    buildUrl(`/api/v1/skills/${encodeURIComponent(skillId)}/attachments`),
    {
      method: 'POST',
      headers: { ...authHeader() },
      credentials: 'include',
      body: form,
    },
  );
  if (!response.ok) {
    throw await parseProblem(response);
  }
  return (await response.json()) as SkillAttachmentDto;
}

export async function deleteSkillAttachment(
  skillId: string,
  filename: string,
): Promise<void> {
  const response = await fetch(
    buildUrl(
      `/api/v1/skills/${encodeURIComponent(skillId)}/attachments/${encodeURIComponent(filename)}`,
    ),
    {
      method: 'DELETE',
      headers: { ...authHeader() },
      credentials: 'include',
    },
  );
  if (!response.ok) {
    throw await parseProblem(response);
  }
}
